import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import type { AddFamilyMemberBody } from '@/features/families/api';
import type { CascadeRole } from '@/models/family';

interface AddMemberNewPersonPanelProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (body: AddFamilyMemberBody) => void;
}

const ROLES: readonly CascadeRole[] = ['FAMILY_MEMBER', 'PROBAND'];

export function AddMemberNewPersonPanel({
  busy,
  onCancel,
  onSubmit,
}: AddMemberNewPersonPanelProps): ReactElement {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<CascadeRole>('FAMILY_MEMBER');

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const canSubmit = (trimmedFirst.length > 0 || trimmedLast.length > 0) && !busy;

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        const body: AddFamilyMemberBody = {
          cascade_role: role,
          ...(trimmedFirst.length > 0 ? { first_name: trimmedFirst } : {}),
          ...(trimmedLast.length > 0 ? { last_name: trimmedLast } : {}),
        };
        onSubmit(body);
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="member-first-name">
            {t('routes.family.members.addDialog.firstName')}
          </Label>
          <Input
            id="member-first-name"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="member-last-name">
            {t('routes.family.members.addDialog.lastName')}
          </Label>
          <Input
            id="member-last-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="member-role">{t('routes.family.members.addDialog.role')}</Label>
        <Select
          id="member-role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value as CascadeRole);
          }}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`family.cascadeRole.${r}`)}
            </option>
          ))}
        </Select>
      </div>
      <DialogFooter className="pt-2">
        <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
          {t('ui.dialog.close')}
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          loading={busy}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {t('routes.family.members.addDialog.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
